import { useState, useCallback } from 'react';
import { DefaultPageProps } from '@/types/types/page/pages';
import {
    IWalletPaymentAllDetails,
    IWalletUploadedDocument,
} from '@/types/types/forms/wallet/wallet.interface';
import { WalletPaymentSteps } from '@/types/enums/forms/wallet/walletPaymentSteps';
import { Paper, Button, Typography } from '@mui/material';
import { useAccounts } from '@/hooks/forms/wallet/useAccounts';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import {
    WALLET_PHRASES,
    WALLET_REQUEST_PHRASES,
    WALLET_PAYMENT_DETAILS_PHRASES,
    WALLET_CONFIRMATION_PHRASES,
} from '@/consts/phrases/walletPhrases';
import { NoAccountExists } from '../NoAccountExists/NoAccountExists';
import { Link } from '@/components/general/Link/Link';
import { AccountSelector } from '../AccountSelector/AccountSelector';
import { WalletPaymentRequestForm } from '@/components/forms/forms/wallet/WalletPaymentRequestForm/WalletPaymentRequestForm';
import { WalletPaymentDetailsForm } from '@/components/forms/forms/wallet/WalletPaymentDetailsForm/WalletPaymentDetailsForm';
import { WalletConfirmationDetailsForm } from '@/components/forms/forms/wallet/WalletConfirmationDetailsForm/WalletConfirmationDetailsForm';

import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';

interface PaymentProps {
    dictionary: DefaultPageProps['dictionary'];
}

export const walletFormInitialState: IWalletPaymentAllDetails = {
    currentStep: WalletPaymentSteps.PaymentStart,
    prevStep: WalletPaymentSteps.PaymentStart,
    cachedData: {
        [WalletPaymentSteps.PaymentStart]: {},
        [WalletPaymentSteps.ProvidePaymentDetails]: {},
        [WalletPaymentSteps.ConfirmPaymentDetails]: {},
    },
    data: {
        [WalletPaymentSteps.ProvidePaymentDetails]: {},
        [WalletPaymentSteps.ConfirmPaymentDetails]: {},
    },
};

export const Payment = ({ dictionary }: PaymentProps) => {
    const { t } = usePhrase();

    const [walletPaymentData, setWalletPaymentData] =
        useState<IWalletPaymentAllDetails>(walletFormInitialState);

    const [documents, setDocuments] = useState<Record<string, IWalletUploadedDocument[]>>({});

    const updatePaymentDocumentsById = (id: string, newDocuments: IWalletUploadedDocument[]) => {
        setDocuments((prevDocuments) => ({
            ...prevDocuments,
            [id]: newDocuments,
        }));
    };

    const clearPaymentDocuments = () => setDocuments({});

    const { currentStep } = walletPaymentData;

    const resetWalletPaymentData = useCallback(
        () => setWalletPaymentData(walletFormInitialState),
        [],
    );

    const updateWalletPaymentData = useCallback(
        (data: Partial<IWalletPaymentAllDetails>) =>
            setWalletPaymentData((prevData) => ({ ...prevData, ...data })),
        [],
    );

    const { selectedAccount, accounts, handleChangeAccountTab } =
        useAccounts(resetWalletPaymentData);

    const stepTitle = (() => {
        if (currentStep === WalletPaymentSteps.PaymentStart) {
            return t(WALLET_REQUEST_PHRASES.PAYMENT_REQUEST_TITLE);
        } else if (currentStep === WalletPaymentSteps.ProvidePaymentDetails) {
            return t(WALLET_PAYMENT_DETAILS_PHRASES.PAYMENT_DETAILS_TITLE);
        } else if (currentStep === WalletPaymentSteps.ConfirmPaymentDetails) {
            return t(WALLET_CONFIRMATION_PHRASES.CONFIRMATION_TITLE);
        }
    })();

    return (
        <>
            {accounts && !!accounts.length && selectedAccount && (
                <>
                    <Link href="/wallet" passHref>
                        <Button variant="text" size="small" startIcon={<KeyboardBackspaceIcon />}>
                            {t(WALLET_PHRASES.BACK_TO_TRANSACTIONS)}
                        </Button>
                    </Link>
                    <Typography variant="h1" sx={{ my: 2 }}>
                        {stepTitle}
                    </Typography>
                    <AccountSelector
                        accounts={accounts}
                        tabVal={selectedAccount.accountId}
                        setTabVal={handleChangeAccountTab}
                    />
                    <Paper sx={{ p: 3 }}>
                        {currentStep === WalletPaymentSteps.PaymentStart && (
                            <WalletPaymentRequestForm
                                data={walletPaymentData}
                                selectedAccount={selectedAccount}
                                updateWalletPaymentData={updateWalletPaymentData}
                                clearPaymentDocuments={clearPaymentDocuments}
                            />
                        )}
                        {currentStep === WalletPaymentSteps.ProvidePaymentDetails && (
                            <WalletPaymentDetailsForm
                                data={walletPaymentData}
                                dictionary={dictionary}
                                selectedAccount={selectedAccount}
                                updateWalletPaymentData={updateWalletPaymentData}
                                documents={documents}
                                updatePaymentDocumentsById={updatePaymentDocumentsById}
                            />
                        )}
                        {currentStep === WalletPaymentSteps.ConfirmPaymentDetails && (
                            <WalletConfirmationDetailsForm
                                data={walletPaymentData}
                                selectedAccount={selectedAccount}
                                updateWalletPaymentData={updateWalletPaymentData}
                                documents={documents}
                            />
                        )}
                    </Paper>
                </>
            )}
            {!accounts || (!accounts.length && <NoAccountExists />)}
        </>
    );
};
